// src/components/roleplay/useSpeech.js
// Thin wrapper around the browser Web Speech API (speech-to-text via
// SpeechRecognition, text-to-speech via speechSynthesis). Every call into
// the underlying APIs is wrapped in try/catch and gated behind feature
// detection so callers can always safely fall back to typed input — Chrome
// desktop is the only browser with solid support for both.
import { useCallback, useEffect, useRef, useState } from 'react';

function getRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function getSynth() {
  if (typeof window === 'undefined') return null;
  return window.speechSynthesis || null;
}

export default function useSpeech() {
  const RecognitionCtor = getRecognitionCtor();
  const synth = getSynth();

  const sttSupported = !!RecognitionCtor;
  const ttsSupported = !!synth;

  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);

  const recognitionRef = useRef(null);
  const onResultRef = useRef(null);
  const onEndRef = useRef(null);
  const lastErrorRef = useRef(null);

  const stopListening = useCallback(() => {
    // Suppress the end callback for an intentional stop (we don't want the
    // hands-free loop to auto-restart when WE stopped the mic).
    onEndRef.current = null;
    try {
      recognitionRef.current?.stop();
    } catch {
      // ignore — recognition may already be stopped/unsupported
    }
    setIsListening(false);
  }, []);

  const startListening = useCallback(
    (onResult, onEnd) => {
      if (!sttSupported) return;
      onResultRef.current = onResult;
      onEndRef.current = onEnd || null;
      lastErrorRef.current = null;

      try {
        // Always start from a fresh instance — recognizers don't reliably
        // support being restarted after stop/end on all Chromium builds.
        const recognition = new RecognitionCtor();
        recognition.continuous = false;
        recognition.interimResults = true;
        recognition.lang = 'en-US';

        recognition.onresult = (event) => {
          let finalText = '';
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            if (result.isFinal) {
              finalText += result[0].transcript;
            }
          }
          if (finalText.trim() && onResultRef.current) {
            onResultRef.current(finalText.trim());
          }
        };

        recognition.onerror = (event) => {
          lastErrorRef.current = event?.error || 'error';
          setIsListening(false);
        };

        recognition.onend = () => {
          setIsListening(false);
          const cb = onEndRef.current;
          const err = lastErrorRef.current;
          onEndRef.current = null;
          lastErrorRef.current = null;
          if (cb) cb(err);
        };

        recognitionRef.current = recognition;
        recognition.start();
        setIsListening(true);
      } catch {
        setIsListening(false);
      }
    },
    [RecognitionCtor, sttSupported]
  );

  const cancelSpeak = useCallback(() => {
    try {
      synth?.cancel();
    } catch {
      // ignore
    }
    setIsSpeaking(false);
  }, [synth]);

  const speak = useCallback(
    (text, onDone) => {
      // Always resolve onDone so the caller's conversation loop continues even
      // when TTS is unsupported or errors.
      const done = () => {
        setIsSpeaking(false);
        if (typeof onDone === 'function') onDone();
      };
      if (!ttsSupported || !text) {
        done();
        return;
      }
      try {
        synth.cancel(); // don't stack utterances
        const utterance = new window.SpeechSynthesisUtterance(text);
        const voices = synth.getVoices ? synth.getVoices() : [];
        const englishVoice = voices.find((v) => (v.lang || '').startsWith('en'));
        if (englishVoice) utterance.voice = englishVoice;
        utterance.lang = 'en-US';
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = done;
        utterance.onerror = done;
        synth.speak(utterance);
      } catch {
        done();
      }
    },
    [synth, ttsSupported]
  );

  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch {
        // ignore
      }
      try {
        synth?.cancel();
      } catch {
        // ignore
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sttSupported,
    ttsSupported,
    isListening,
    isSpeaking,
    startListening,
    stopListening,
    speak,
    cancelSpeak,
  };
}
